import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';

import { Text, TextType } from 'ui-components/components/text';

import { SimpleModal } from '../../../../../components/simple-modal';
import * as UserActions from '../../../../../store/user/user.actions';
import { UserSettings } from '../../../../../enums/user-settings/user-settings';
import { getShippedRecentPath } from '../../../../../routes/constants';
import { messages } from './messages';

type PostLabelReminderModalProps = {
  fulfillmentPlanId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  modalFrameworkMethods?: any;
};

/**
 * Modal shown after label creation when the user checked "I'll upload custom
 * documents after label purchase". Offers three options:
 *
 * - "Shipment Details" - navigates to the shipment details page and closes
 * - "Don't Show Again" - saves user preference via saveUserSetting action and closes
 * - "Do This Later" - just closes
 *
 * Launched via ModalService from the label purchase helper (showPostLabelReminderIfNeeded).
 */
export const PostLabelReminderModal = ({ fulfillmentPlanId, modalFrameworkMethods }: PostLabelReminderModalProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const closeModal = useCallback(() => {
    modalFrameworkMethods?.closeModal();
  }, [modalFrameworkMethods]);

  const handleGoToShipmentDetails = useCallback(() => {
    closeModal();
    navigate(`${getShippedRecentPath()}/shipment/${fulfillmentPlanId}`);
  }, [closeModal, navigate, fulfillmentPlanId]);

  const handleDontShowAgain = useCallback(() => {
    dispatch(
      UserActions.saveUserSetting({
        settingName: UserSettings.customsDocumentReminderDismissed.settingName,
        settingValue: true,
        excludeV3prefix: true,
      }),
    );
    closeModal();
  }, [dispatch, closeModal]);

  return (
    <SimpleModal
      headerMessage={messages.postLabelReminderTitle}
      onClose={closeModal}
      size='medium'
      actionMessage={messages.postLabelReminderShipmentDetails}
      onAction={handleGoToShipmentDetails}
      secondActionMessage={messages.postLabelReminderDontShowAgain}
      onSecondAction={handleDontShowAgain}
      cancelMessage={messages.postLabelReminderDoThisLater}
      onCancel={closeModal}
    >
      <Text textType={TextType.Paragraph} value={messages.postLabelReminderBody} />
    </SimpleModal>
  );
};
